import React, { useCallback, useMemo } from "react";
import { Controller, useWatch } from "react-hook-form";
import { ErrorLabel } from "../../../form-inputs/shared/error-label";
import { Select, Textarea } from "../../../form-inputs/inputs";
import { availableTaskOptions } from "../../lib/constants";
import { TaskType } from "../../../../shared/taskTypeMap";

export const TaskReasonFormFields = ({
    index,
    control,
    selectedTask,
    selectedTasks,
    error,
    multiRejectReason,
    rejectReasonOptions,
    onValidateSelection,
    allowedTaskOptions,
}) => {
    const selectedReason = useWatch({
        control,
        name: `rejectTasks[${index}].reason`,
    });
    const { task, reason, other } = error;

    const verifyTaskRules = useCallback(
        (currentlySelectedItem, listItem) => {
            switch (currentlySelectedItem) {
                case TaskType.PHOTOGRAPHY:
                    return listItem !== TaskType.IMAGE_EDITING_QA;
                case TaskType.IMAGE_EDITING_QA:
                    return listItem !== TaskType.PHOTOGRAPHY;
                default:
                    return selectedTasks.values().some((value) => value === currentlySelectedItem);
            }
        },
        [selectedTasks]
    );

    const taskOptions = useMemo(
        () =>
            availableTaskOptions
                .filter((availableTask) => {
                    return allowedTaskOptions.includes(availableTask.value);
                })
                .filter((availableTask) => {
                    const selectedOption = selectedTasks.get(index);

                    // only the first row with a selected task needs to have all options available - even when a task was already selected
                    if (availableTask.value === selectedOption || (selectedTasks.size === 1 && selectedOption)) {
                        return true;
                    }

                    return [...selectedTasks.entries()].every(([, value]) => {
                        return availableTask.value !== value && verifyTaskRules(value, availableTask.value);
                    });
                }),
        [allowedTaskOptions, index, selectedTasks, verifyTaskRules]
    );

    return (
        <>
            <div className="flex justify-content-between align-start">
                <div className="flex-column flex-1 m-r-28">
                    <div className="flex-1 txt-bold">Reject task to:</div>
                    <Controller
                        name={`rejectTasks[${index}].task`}
                        control={control}
                        rules={{
                            required: "Please select task type",
                        }}
                        render={({ field }) => (
                            <ErrorLabel errorMessage={task?.message}>
                                <Select
                                    {...field}
                                    onChange={(e) => {
                                        onValidateSelection(e.target.value, index);

                                        return field.onChange(e.target.value);
                                    }}
                                    className="flex-2"
                                    options={taskOptions}
                                />
                            </ErrorLabel>
                        )}
                    />
                </div>
                <div className="flex-column flex-2">
                    <div className="flex-1 txt-bold">Reject Reason:</div>
                    <Controller
                        name={`rejectTasks[${index}].reason`}
                        rules={{
                            required: "Please select reject reason",
                        }}
                        control={control}
                        disabled={!selectedTask || multiRejectReason}
                        render={({ field }) => (
                            <ErrorLabel errorMessage={reason?.message}>
                                <Select
                                    {...field}
                                    className="flex-2"
                                    options={rejectReasonOptions}
                                />
                            </ErrorLabel>
                        )}
                    />
                </div>
            </div>
            {selectedReason === "0" && !multiRejectReason && (
                <div className="flex">
                    <div className="flex-column flex-1 m-r-28" />
                    <div className="flex-column flex-2">
                        <div className="flex-1 txt-bold">Reject Reason (Other):</div>
                        <Controller
                            name={`rejectTasks[${index}].other`}
                            rules={{
                                required: "Please select reject reason",
                            }}
                            control={control}
                            disabled={!selectedTask}
                            render={({ field }) => (
                                <ErrorLabel errorMessage={other?.message}>
                                    <Textarea
                                        {...field}
                                        rows={2}
                                        className="flex-2"
                                    />
                                </ErrorLabel>
                            )}
                        />
                    </div>
                </div>
            )}
        </>
    );
};
